import type { OrderRecord } from '../../types/orders';
import {
  buildOrderStory,
  type StoryStep,
  type StoryStepStatus,
} from './orderStory';

interface OrderStoryStripProps {
  order: OrderRecord;
  hasInvoice: boolean;
  formatDate: (iso: string | null | undefined) => string;
}

interface NodeColors {
  circleBg: string;
  circleBorder: string;
  glyphColor: string;
  labelColor: string;
}

const NODE_COLORS: Record<StoryStepStatus, NodeColors> = {
  done: {
    circleBg: '#2563eb',
    circleBorder: '#2563eb',
    glyphColor: '#ffffff',
    labelColor: '#1e293b',
  },
  current: {
    circleBg: '#ffffff',
    circleBorder: '#2563eb',
    glyphColor: '#2563eb',
    labelColor: '#1e3a8a',
  },
  pending: {
    circleBg: '#ffffff',
    circleBorder: '#d1d5db',
    glyphColor: '#9ca3af',
    labelColor: '#9ca3af',
  },
  skipped: {
    circleBg: '#f9fafb',
    circleBorder: '#e5e7eb',
    glyphColor: '#cbd5e1',
    labelColor: '#cbd5e1',
  },
};

const CIRCLE_SIZE = 28;

/**
 * Horizontal 9-step "order story" strip: Email → Classified → Agent Decision →
 * Draft → Submitted → Live in Sage → Acknowledged → Shipped → Invoiced.
 * Pure rendering — the per-step status logic lives in ./orderStory.
 */
export const OrderStoryStrip: React.FC<OrderStoryStripProps> = ({
  order,
  hasInvoice,
  formatDate,
}) => {
  const { steps, cancelled } = buildOrderStory(order, hasInvoice);

  return (
    <div
      data-testid="order-story-strip"
      style={{
        padding: '16px 4px 8px',
        marginBottom: 16,
        borderBottom: '1px solid #e5e7eb',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 12,
        }}
      >
        <div
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: '#6b7280',
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
          }}
        >
          Order Story
        </div>
        {cancelled && (
          <span
            data-testid="order-story-cancelled"
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: '#b91c1c',
              background: '#fee2e2',
              borderRadius: 999,
              padding: '2px 10px',
            }}
          >
            Cancelled
          </span>
        )}
      </div>

      {/* width:0 + minWidth:100% keeps the strip's intrinsic width from
          widening the page on phones; the steps scroll sideways instead of
          squeezing their labels into each other (kanban fadc070c). */}
      <div style={{ overflowX: 'auto', width: 0, minWidth: '100%' }}>
        <ol
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            listStyle: 'none',
            margin: 0,
            padding: 0,
            minWidth: '100%',
          }}
        >
          {steps.map((step, i) => (
            <li
              key={step.key}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                flex: '1 1 0',
                minWidth: 84,
              }}
            >
              {i > 0 && (
                <Connector reached={steps[i - 1].status === 'done'} />
              )}
              <StoryNode step={step} formatDate={formatDate} />
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
};

const Connector: React.FC<{ reached: boolean }> = ({ reached }) => (
  <div
    aria-hidden
    style={{
      flex: '1 1 12px',
      minWidth: 12,
      height: 2,
      marginTop: CIRCLE_SIZE / 2 - 1,
      background: reached ? '#2563eb' : '#e5e7eb',
    }}
  />
);

const STATUS_GLYPH: Record<StoryStepStatus, string> = {
  done: '✓',
  current: '●',
  pending: '',
  skipped: '–',
};

const StoryNode: React.FC<{
  step: StoryStep;
  formatDate: (iso: string | null | undefined) => string;
}> = ({ step, formatDate }) => {
  const colors = NODE_COLORS[step.status];
  const subtitle = step.note ?? (step.timestamp ? formatDate(step.timestamp) : null);
  return (
    <div
      data-testid={`story-step-${step.key}`}
      data-status={step.status}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        flex: '1 1 0',
        minWidth: 64,
        textAlign: 'center',
        padding: '0 2px',
      }}
    >
      <div
        style={{
          width: CIRCLE_SIZE,
          height: CIRCLE_SIZE,
          borderRadius: '50%',
          background: colors.circleBg,
          border: `2px solid ${colors.circleBorder}`,
          color: colors.glyphColor,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: step.status === 'current' ? 10 : 14,
          fontWeight: 700,
          lineHeight: 1,
          flexShrink: 0,
        }}
      >
        {STATUS_GLYPH[step.status]}
      </div>
      <div
        style={{
          fontSize: 11,
          fontWeight: step.status === 'done' || step.status === 'current' ? 600 : 500,
          color: colors.labelColor,
          marginTop: 6,
          lineHeight: 1.2,
        }}
      >
        {step.label}
      </div>
      {subtitle && (
        <div
          style={{
            fontSize: 10,
            color: '#9ca3af',
            marginTop: 2,
            lineHeight: 1.2,
          }}
        >
          {subtitle}
        </div>
      )}
    </div>
  );
};
